import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useEffect, useState, useMemo } from "react";
import GenericTierTwo from "../GenericTierTwo";
import GenericModule from "../GenericModule";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";

import {
	initialValues,
	validationSchema,
	getSchemas,
	t2InitialValues,
} from "./otherLand";
import {
	useAppSelector,
	useThreadsProcessor,
} from "@app/hooks";
import { BEModulesClassNames } from "@utils/moduleList";
import {
	useContextAwareGetOtherLandDefaults,
	useContextAwareGetOtherLand,
	useUpdateOtherLandMutation,
} from "@hooks/index";
import { selectCurrentProject } from "@features/project/projectSlice";
import type { ModuleFull } from "./otherLandTypes";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import useSpecialLandTypeInput from "../../hooks/useSpecialLandTypeInput";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import { FEModules } from "@utils/moduleList";

const OtherLand = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const id = moduleList.find(({ id }) => id === FEModules.OtherLand)?.uniqueId;

	const {
		data: otherLandData,
		isLoading,
		isError,
		error,
	} = useContextAwareGetOtherLand(id ?? 0, { refetchOnMountOrArgChange: true });
	const [
		updateOtherLand,
		{ error: updateError, isError: isUpdateError, isLoading: isLoadingUpdate },
	] = useUpdateOtherLandMutation();
	const { input, lucData, isLoadingLuc } = useSpecialLandTypeInput();

	const { data: defaults, error: defaultsError, isError: isDefaultsError, isLoading: isLoadingDefaults } = useContextAwareGetOtherLandDefaults(id ?? 0, { skip: !id });
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });

	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();
	const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
	const [initValues, setInitValues] = useState<ModuleFull>({
		module: initialValues,
		tiertwo: t2InitialValues,
	});
	const { errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingDefaults, isLoadingLuc],
		isMutateError: isUpdateError,
		mutateError: updateError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });

	useEffect(() => {
		if (otherLandData) {
			setInitValues({
				module: otherLandData.module,
				tiertwo: { ...otherLandData.tiertwo, ...defaults },
				luc: lucData ? lucData.module : undefined,
			});
			processModuleThreads({
				module: otherLandData.module,
			})
		}
	}, [otherLandData, defaults, processModuleThreads, lucData]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const module = { ...values.module };
		const payload = {
			data: { ...values, module },
			activityId: activeActivityId ?? 0,
		};

		try {
			values.module.id && (await updateOtherLand(payload));
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<GenericModule
					title="project.other_land"
					t2Schema={t2Schema}
					moduleSchema={moduleSchema}
					beModuleType={BEModulesClassNames.OtherLand}
					isLoading={isLoadingUpdate}
					footerError={errorMsg}
					blockingError={blockingError}
					isError={isUpdateError}
				>
					{() => (
						<div className="py-2">
							<GenericInputGroup inputGroup={input} />
						</div>
					)}
				</GenericModule>

				<GenericTierTwo
					title="project.other_land"
					t2Schema={t2Schema}
					warningMessage={defaultsErrorMsg ?? null}
				/>
			</>
		</Formik>
	);
};

export default OtherLand;
